import { useEffect, useMemo } from "react"

import { useQueryClient } from "@tanstack/react-query"

import type { HostedAgentSession } from "@/api/client/hosted-agents"

import { queryKeys } from "@/lib/query-keys"

import { agentThreadTitleFromMessage } from "../utils/agents-utils"

/**
 * Client-side thread title on the first turn (#3165).
 *
 * The backend titles a thread a few seconds after the first turn lands. Until
 * it does, the rail would read "Untitled thread" for the thread the user is
 * looking at, so the title derived from the first user message is written into
 * the rail's cache. A title the server has already set is never overwritten —
 * it stays authoritative, and the next list refetch replaces this stand-in.
 *
 * Two rules keep this from looping. The rail OBSERVES the query this hook
 * writes to, so every write re-renders the view that calls the hook:
 *
 * 1. The key is memoised. `queryKeys.hostedAgentSessions` builds a fresh array
 *    on every call, so as a raw effect dependency it re-ran the effect after
 *    every render — and every run wrote to the cache.
 * 2. The effect writes only when it has something to stamp. A cache write is
 *    the loop's fuel: even a write that changes no row still notifies the
 *    rail's observers.
 *
 * Without both, opening a thread that already has a first user message crashed
 * the page with React's "Maximum update depth exceeded".
 */
export function useAgentThreadTitle(
  agentSlug: string,
  sessionId: string | null | undefined,
  firstUserMessage: string | undefined
) {
  const queryClient = useQueryClient()
  const key = useMemo(() => queryKeys.hostedAgentSessions(agentSlug), [agentSlug])
  // A list refetch that lands before the backend has titled the thread must be
  // re-stamped, so the cached rows are a dependency of the effect below.
  const cached = queryClient.getQueryData<HostedAgentSession[]>(key)
  useEffect(() => {
    if (!sessionId) return
    const derived = agentThreadTitleFromMessage(firstUserMessage)
    if (!derived) return
    const rows = queryClient.getQueryData<HostedAgentSession[]>(key)
    if (!rows) return
    const index = rows.findIndex((row) => row.id === sessionId)
    if (index < 0 || rows[index].title?.trim()) return
    const next = [...rows]
    next[index] = { ...next[index], title: derived }
    queryClient.setQueryData<HostedAgentSession[]>(key, next)
  }, [cached, firstUserMessage, key, queryClient, sessionId])
}
